import React, {useEffect, useState, useCallback} from 'react';
import PropTypes from 'prop-types';
import {Field} from 'redux-form';
import {get as _get} from 'lodash';
import moment from 'moment';

import {FiltersWrapper} from '@appComponents/HOCFiltersWrapper';
import Form from '@appComponents/ReduxFormControls';
import {DateRangeFilter} from '@utils/commonFilters/DateRangeFilter';
import {getTodayDate, getEndOfTodayDate} from '@utils/date';

const ChangelogFiltersComponent = ({filters, restoreFilterDateValues, restoreFilterValues, loading, data}) => {
    const [fromMaxDate, setFromMaxDate] = useState(null);
    const [fromMaxDateDefault, setFromMaxDateDefault] = useState(null);
    const [fromMaxTime, setFromMaxTime] = useState(moment().hours(23).minutes(59));

    const [toMinDate, setToMinDate] = useState(null);
    const [toMinDateDefault, setToMinDateDefault] = useState(null);
    const [toMinTime, setToMinTime] = useState(moment().hours(0).minutes(0));

    useEffect(() => {
        const rawFromMaxDate = restoreFilterDateValues(filters?.Changelog, 'to');
        const rawToMinDate = restoreFilterDateValues(filters?.Changelog, 'from', moment());

        const from = moment.isMoment(rawFromMaxDate) ? rawFromMaxDate : moment(rawFromMaxDate);
        const to = moment.isMoment(rawToMinDate) ? rawToMinDate : moment(rawToMinDate);

        if (from && !moment(fromMaxDateDefault).isSame(from)) {
            setFromMaxDateDefault(from);
            setFromMaxDate(from);
        }

        if (to && !moment(toMinDateDefault).isSame(to)) {
            setToMinDateDefault(to);
            setToMinDate(to);
        }

        if (
            from?.format('YYYY-MM-DD') === to?.format('YYYY-MM-DD')
        ) {
            setFromMaxTime(from);
            setToMinTime(to);
        }
    }, [filters, restoreFilterDateValues]);

    const onChange = useCallback((date, name) => {
        if ('from' === name) {
            setToMinDate(date);
            setFromMaxTime(null);
            setToMinTime(null);

            if (date.format('YYYY-MM-DD') === fromMaxDate?.format('YYYY-MM-DD')) {
                setFromMaxTime(fromMaxDate);
                setToMinTime(date);
            }
        } else if ('to' === name) {
            setFromMaxDate(date);
            setFromMaxTime(null);
            setToMinTime(null);

            if (date.format('YYYY-MM-DD') === toMinDate?.format('YYYY-MM-DD')) {
                setFromMaxTime(date);
                setToMinTime(toMinDate);
            }
        }
    }, [fromMaxDate, toMinDate]);

    return (
        <div className='changelog__filters filter__controls__container'>
            <DateRangeFilter
                loading={loading}
                filters={filters?.Changelog}
                restoreFilterDateValues={restoreFilterDateValues}
                onChange={onChange}
                dateConfig={{
                    fromMaxDate,
                    fromMaxDateDefault,
                    fromMaxTime,
                    toMinDate,
                    toMinDateDefault,
                    toMinTime,
                }}
            />
            <Field
                component={Form.ReduxSelect}
                name='resources'
                options={_get(data, 'securedResources', [])}
                placeholder='Resource'
                disabled={loading}
                search
                multiple
                setValue={restoreFilterValues(filters?.Changelog, 'resources')}
            />
            <Field
                component={Form.ReduxInput}
                name='elementId'
                placeholder='Element ID'
                disabled={loading}
                setValue={restoreFilterValues(filters?.Changelog, 'elementId')}
            />
            <Field
                component={Form.ReduxInput}
                name='elementName'
                placeholder='Element name'
                disabled={loading}
                setValue={restoreFilterValues(filters?.Changelog, 'elementName')}
            />
            <Field
                component={Form.ReduxSelect}
                name='actions'
                placeholder='Action'
                options={_get(data, 'changelogActionTypes', [])}
                disabled={loading}
                multiple
                setValue={restoreFilterValues(filters?.Changelog, 'actions')}
            />
            <Field
                component={Form.ReduxSelect}
                name='users'
                placeholder='User'
                disabled={loading}
                search
                options={_get(data, 'changelogUsers', [])}
                multiple
                setValue={restoreFilterValues(filters?.Changelog, 'users')}
            />
            <Field
                component={Form.ReduxSelect}
                name='apiUsers'
                placeholder='API user'
                disabled={loading}
                search
                options={_get(data, 'apiUsers', [])}
                multiple
                setValue={restoreFilterValues(filters?.Changelog, 'apiUsers')}
            />
        </div>
    );
};

ChangelogFiltersComponent.propTypes = {
    filters: PropTypes.shape({
        Changelog: PropTypes.object,
    }),
    loading: PropTypes.bool.isRequired,
    data: PropTypes.object,
    restoreFilterValues: PropTypes.func,
    restoreFilterDateValues: PropTypes.func,
};

const ChangelogFilters = FiltersWrapper(ChangelogFiltersComponent, {
    name: 'Changelog',
    onSubmit: ChangelogFiltersComponent.onSubmit,
    initialValues: {
        from: getTodayDate(),
        to: getEndOfTodayDate(),
    },
});

export default ChangelogFilters;
